import _get from 'lodash/get';
import { LAYOUT_ENTITIES } from '@bedrock/fido-sdk';
import config from '@bedrock-rdk/config';
import { sendEvent, setAttributes } from '../logic/analytics/analytics.utils';
import {
  AUDIO_TRACK_CHANGED,
  PLAYING,
  PAUSED,
  INITIALIZED,
  WAITING,
  SEEKING,
  ENDED,
  SUBTITLES_TRACK_CHANGED,
  TIME_UPDATE,
  TOP_BAR_RELATED_VIDEOS,
  TOP_BAR_TRACKS,
  TOP_BAR_START_OVER,
  TOP_BAR_BACK_TO_LIVE,
  TOP_BAR_BACK,
  SEEKED,
  TRACKS_CHANGED,
  QUALITY_CHANGED,
  PLAYER_INFO,
  PERCENTAGE_EVENT_DURATION,
  RESET,
} from '../logic/player/player.constants';
import { getIsStarted, getVideoSource, getCurrentAssetType, getTitle } from '../logic/player/player.selectors';
import { FETCH_PROFILE_INFOS_SUCCESS } from '../logic/user/user.actions';

export const ACTION_TO_TAGGING_PLAN = {
  [TOP_BAR_RELATED_VIDEOS]: 'pressViewRelatedVideo',
  [TOP_BAR_TRACKS]: 'pressAudioSettingsVideo',
  [TOP_BAR_START_OVER]: 'pressStartOverVideo',
  [TOP_BAR_BACK_TO_LIVE]: 'pressBackToLiveVideo',
  [TOP_BAR_BACK]: 'pressBackButtonDuringVideo',
};

// Completion steps already reported for the current video, the audio contents are not reported
let reportedCompletions = [];

const reportCompletion = ({ player }) => {
  const { contentType, durationInSeconds: duration, elapsedTimeInSeconds: elapsed } = player;

  if (!duration || contentType === LAYOUT_ENTITIES.audio || contentType === LAYOUT_ENTITIES.radio) {
    return;
  }

  for (let percent = PERCENTAGE_EVENT_DURATION; percent <= 100; percent += PERCENTAGE_EVENT_DURATION) {
    // NOTE: the last step is moved 5 seconds earlier to make sure it is reported
    const threshold = (duration * percent) / 100 - (percent === 100 ? 5 : 0);

    if (elapsed >= threshold && !reportedCompletions.includes(percent)) {
      reportedCompletions.push(percent);
      sendEvent({ key: `completion${percent}` });
    }
  }
};

export default ({ getState }) =>
  next =>
  action => {
    switch (action.type) {
      case FETCH_PROFILE_INFOS_SUCCESS: {
        setAttributes({ isKidsProfile: action.isKidsProfile, userProfile: action.profileUid });
        sendEvent({ key: 'profileSelected' });

        break;
      }
      case INITIALIZED: {
        const analytics = action.item.analytics || {};
        const progress = _get(action.item, 'video.progress') || {};
        const currentTime = progress.tcResume || 0;

        setAttributes({
          currentTime,
          isLive: action.isLive,
          duration: action.item.video.duration,
          videoEndScreen: progress.endscreenDisplayTc || 0,
          newProgramAnalyticsConfiguration: analytics,
          contentType: action.entityType,
        });

        break;
      }
      case PLAYING: {
        sendEvent({ key: 'playVideo' });

        break;
      }
      case AUDIO_TRACK_CHANGED: {
        const state = getState();
        const originalLanguage = _get(state.player, 'originalLanguage') || config.language;

        const language = _get(action, 'payload.currentTrack.language');
        setAttributes({
          playerActiveAudioLanguage: language,
          player_audio: language,
          switch_language: originalLanguage === language ? 'no' : 'yes',
        });
        sendEvent({ key: 'audioTrackChanged', language });

        break;
      }
      case SUBTITLES_TRACK_CHANGED: {
        const language = _get(action, 'payload.currentTrack.language');
        setAttributes({
          playerActiveTextLanguage: language,
          subtitle_active: language ? 'yes' : 'no',
          player_subtitles: language,
        });
        sendEvent({ key: 'subtitlesTrackChanged', language });

        break;
      }
      case TIME_UPDATE: {
        const state = getState();
        const isStarted = getIsStarted(state);

        if (!isStarted) {
          sendEvent({ key: 'autoPlay' });
        }

        setAttributes({
          currentTime: action.payload.currentTime - (state.player.startTimeInSeconds || 0),
          videoThroughput: action.payload.throughput,
        });

        break;
      }

      case WAITING: {
        const isBuffering = _get(action, 'payload.isBuffering') || false;
        sendEvent({ key: isBuffering ? 'buffering' : 'bufferingEnded' });

        break;
      }
      case SEEKING: {
        sendEvent({ key: 'seek' });

        break;
      }
      case SEEKED: {
        const state = getState();

        if (!state.player.isLive) {
          setAttributes({ currentTime: (action.payload.to || 0) - (state.player.startTimeInSeconds || 0) });
        }

        sendEvent({ key: 'seekEnded' });

        break;
      }
      case PAUSED: {
        const state = getState();

        setAttributes({
          duration: state.player.durationInSeconds,
        });
        sendEvent({ key: 'pauseVideo' });

        break;
      }
      case ENDED: {
        sendEvent({ key: 'complete' });

        break;
      }
      case QUALITY_CHANGED: {
        const { bitrate, width, height } = action.payload || {};

        setAttributes({ videoBitrate: bitrate, videoWidth: width, videoHeight: height });

        break;
      }
      case PLAYER_INFO: {
        const { name, version } = action.payload || {};

        setAttributes({ playerName: name, playerVersion: version });

        break;
      }
      default:
        break;
    }

    const key = ACTION_TO_TAGGING_PLAN[action.type];
    if (key) {
      sendEvent({ key });
    }

    const result = next(action);

    // NOTE: the video source will be selected after the INITIALIZED action dispatched in a newly reduced state

    switch (action.type) {
      case RESET: {
        reportedCompletions = [];

        break;
      }
      case TIME_UPDATE: {
        reportCompletion(getState());

        break;
      }
      case INITIALIZED: {
        const state = getState();
        const source = getVideoSource(state);

        if (source) {
          setAttributes({
            videoQuality:
              source.video_quality && typeof source.video_quality === 'string'
                ? source.video_quality.toLowerCase()
                : null,
            assetType: getCurrentAssetType(state),
            title: getTitle(state),
            // The manifest reference (parse.manifest lets Youbora resolve the real CDN behind it)
            resource: source.reference,
          });

          sendEvent({ key: 'playbackStarted', analytics: action.item.analytics });
        }
        break;
      }
      case TRACKS_CHANGED: {
        const state = getState();
        const { player } = state;
        const { originalLanguage, selectedSubtitlesTrack, selectedAudioTrack } = player;
        const activeSubtitlesTrack = _get(action, 'payload.subtitleTracks').find(
          ({ id }) => id === selectedSubtitlesTrack,
        );
        const activeAudioTrack = _get(action, 'payload.audioTracks').find(({ id }) => id === selectedAudioTrack);

        setAttributes({
          subtitle_active: activeSubtitlesTrack ? 'yes' : 'no',
          player_audio: activeAudioTrack ? activeAudioTrack.language : null,
          player_subtitles: activeSubtitlesTrack ? activeSubtitlesTrack.language : null,
          switch_language: originalLanguage === activeAudioTrack?.language ? 'no' : 'yes',
        });
        break;
      }
      default:
        break;
    }

    return result;
  };
